import { useCallback, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';

import { useNetworkInformation } from '../utils/useNetworkInformation';

import type { FC } from 'react';

import * as styles from './VideoPreview.module.scss';

interface VideoPreviewProps {
  url: string;
  className?: string;
  onEnded?: () => void;
}

export const VideoPreview: FC<VideoPreviewProps> = ({
  url,
  className,
  onEnded,
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const ref = useRef<HTMLVideoElement>(null);
  const { shouldSaveData } = useNetworkInformation();

  useEffect(() => {
    const video = ref.current;

    if (!video) {
      return;
    }

    const observer = new IntersectionObserver(
      async ([entry]) => {
        if (entry.isIntersecting) {
          video.play().catch(() => null);
        } else {
          video.pause();
        }
      },
      { threshold: 0.8 }
    );

    observer.observe(video);

    return () => observer.disconnect();
  }, []);

  const handleEnded = useCallback(() => {
    onEnded?.();

    return ref.current?.play().catch(() => null);
  }, [onEnded]);

  if (shouldSaveData) {
    return null;
  }

  return (
    <div
      className={classNames(className, styles.container, {
        [styles.playing]: isPlaying,
      })}
    >
      <video
        ref={ref}
        className={styles.video}
        controls={false}
        muted
        playsInline
        preload="none"
        disablePictureInPicture
        disableRemotePlayback
        x-webkit-airplay="deny"
        x-webkit-wirelessvideoplaybackdisabled="true"
        src={url}
        onPlay={() => setIsPlaying(() => true)}
        onPause={() => setIsPlaying(() => false)}
        onEnded={handleEnded}
      />
    </div>
  );
};
